import React, { useEffect, useMemo, useState } from "react";

import { DpRoster, DpRosterVolume, SHIFT_LABELS, StoreShift, WEEKDAY_LABELS } from "../api/scheduleApi";

type Row = { volume: string; morningPct: string };

const EMPTY: Row = { volume: "", morningPct: "50" };
/** Semana da empresa: segunda a domingo. */
const WEEK_ORDER = [1, 2, 3, 4, 5, 6, 0];

const daysOf = (year: number, month: number) => {
  const last = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return Array.from({ length: last }, (_, i) => new Date(Date.UTC(year, month - 1, i + 1)));
};
const isoOf = (d: Date) => d.toISOString().slice(0, 10);

/** Mesma conta do backend (`demandFromVolumes`): teto(volume × % ÷ (produtividade × horas de trabalho do turno)). */
const need = (shifts: StoreShift[], productivity: number, volume: number, morningPct: number) => {
  const active = shifts.filter((s) => s.isActive);
  return active.map((s) => {
    const pct = active.length === 1 ? 100 : s.code === "MANHA" ? morningPct : 100 - morningPct;
    const end = s.endMinute <= s.startMinute ? s.endMinute + 1440 : s.endMinute; // turno que vira o dia
    const capacity = (productivity * Math.max(0, end - s.startMinute - s.breakMinutes)) / 60;
    return { code: s.code, people: capacity > 0 ? Math.ceil((volume * pct) / 100 / capacity - 1e-9) : 0 };
  });
};

type Props = {
  roster: DpRoster;
  busy: boolean;
  onSave: (days: DpRosterVolume[]) => void;
  onClose: () => void;
};

/**
 * Previsão de volume do mês para o hora x homem: o usuário informa o volume e a % da
 * manhã de cada dia da semana, aplica ao mês e ajusta datas específicas.
 */
export default function RosterVolumesModal({ roster, busy, onSave, onClose }: Props) {
  const days = useMemo(() => daysOf(roster.year, roster.month), [roster.year, roster.month]);
  const [week, setWeek] = useState<Record<number, Row>>({});
  const [byDate, setByDate] = useState<Record<string, Row>>({});
  const [error, setError] = useState("");

  useEffect(() => {
    const saved = new Map(roster.volumes.map((v) => [v.date, v]));
    const dates: Record<string, Row> = {};
    const template: Record<number, Row> = {};
    for (const d of days) {
      const v = saved.get(isoOf(d));
      dates[isoOf(d)] = v ? { volume: String(v.volume), morningPct: String(v.morningPct) } : { ...EMPTY };
      if (v && !template[d.getUTCDay()]) template[d.getUTCDay()] = { volume: String(v.volume), morningPct: String(v.morningPct) };
    }
    for (const w of WEEK_ORDER) template[w] = template[w] ?? { ...EMPTY };
    setByDate(dates);
    setWeek(template);
  }, [roster.volumes, days]);

  const applyWeek = () => {
    setError("");
    setByDate(Object.fromEntries(days.map((d) => [isoOf(d), { ...week[d.getUTCDay()] }])));
  };

  const save = () => {
    const out: DpRosterVolume[] = [];
    for (const d of days) {
      // Dia sem valor próprio herda a semana padrão (não precisa clicar em "Aplicar" antes de salvar).
      const own = byDate[isoOf(d)];
      const row = own && own.volume.trim() !== "" ? own : week[d.getUTCDay()];
      if (!row || row.volume.trim() === "") continue;
      const volume = Number(row.volume);
      const morningPct = Number(row.morningPct);
      const label = d.toLocaleDateString("pt-BR", { timeZone: "UTC" });
      if (!Number.isInteger(volume) || volume < 0) return setError(`${label}: volume deve ser um número inteiro.`);
      if (!Number.isInteger(morningPct) || morningPct < 0 || morningPct > 100) return setError(`${label}: a % da manhã vai de 0 a 100.`);
      out.push({ date: isoOf(d), volume, morningPct });
    }
    setError("");
    onSave(out);
  };

  const hint = (row?: Row) => {
    if (!row || row.volume.trim() === "") return "";
    const v = Number(row.volume);
    const p = Number(row.morningPct);
    if (!Number.isFinite(v) || !Number.isFinite(p)) return "";
    return need(roster.shifts, roster.currentProductivity, v, p)
      .map((n) => `${SHIFT_LABELS[n.code].slice(0, 1)} ${n.people}`)
      .join(" · ");
  };

  const pctInput = (row: Row, set: (r: Row) => void, aria: string) => (
    <input
      type="number"
      min={0}
      max={100}
      className="dpInput"
      aria-label={aria}
      value={row.morningPct}
      onChange={(e) => set({ ...row, morningPct: e.target.value })}
      disabled={busy}
    />
  );

  return (
    <div className="dpModalOverlay">
      <div className="dpModal dpModal--wide">
        <header className="dpModal__header">
          <h2 className="dpModal__title">
            Informar volumes{" "}
            <span
              className="dpFormHint"
              style={{ fontWeight: 400, marginLeft: 8 }}
              title="Definido em Administração do sistema; vale na próxima sugestão."
            >
              · Hora x homem: <strong>{roster.currentProductivity} pedidos/h</strong>
            </span>
          </h2>
          <button className="dpModal__close" onClick={onClose} disabled={busy}>
            &times;
          </button>
        </header>
        <div className="dpModal__body">
          <p className="dpFormHint">
            Previsão de pedidos por dia e a parte da manhã (a noite fica com o resto). Pessoas no turno = volume do turno ÷ (
            {roster.currentProductivity} pedidos/hora × horas de trabalho do turno), arredondado para cima. A produtividade se altera em
            Administração do sistema.
          </p>

          <h3 className="dpSection__title">Semana padrão</h3>
          <div className="dpTableWrapper">
            <table className="dpTable">
              <thead>
                <tr>
                  <th>Dia</th>
                  <th>Volume</th>
                  <th>% manhã</th>
                  <th>% noite</th>
                  <th>Pessoas</th>
                </tr>
              </thead>
              <tbody>
                {WEEK_ORDER.map((w) => {
                  const row = week[w] ?? EMPTY;
                  const set = (r: Row) => setWeek((prev) => ({ ...prev, [w]: r }));
                  return (
                    <tr key={w}>
                      <td>{WEEKDAY_LABELS[w]}</td>
                      <td>
                        <input
                          type="number"
                          min={0}
                          className="dpInput"
                          aria-label={`Volume de ${WEEKDAY_LABELS[w]}`}
                          value={row.volume}
                          onChange={(e) => set({ ...row, volume: e.target.value })}
                          disabled={busy}
                        />
                      </td>
                      <td>{pctInput(row, set, `% da manhã de ${WEEKDAY_LABELS[w]}`)}</td>
                      <td>{Number.isFinite(Number(row.morningPct)) ? 100 - Number(row.morningPct) : ""}%</td>
                      <td>{hint(row)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <button type="button" className="dpBtn dpBtn--secondary" onClick={applyWeek} disabled={busy} style={{ marginTop: 8 }}>
            Aplicar a todos os dias do mês
          </button>

          <h3 className="dpSection__title" style={{ marginTop: 16 }}>
            Dia a dia
          </h3>
          <p className="dpFormHint">Ajuste uma data específica (evento, feriado). Dia deixado em branco usa a semana padrão ao salvar.</p>
          <div className="dpTableWrapper" style={{ maxHeight: 320, overflowY: "auto" }}>
            <table className="dpTable">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Volume</th>
                  <th>% manhã</th>
                  <th>Pessoas</th>
                </tr>
              </thead>
              <tbody>
                {days.map((d) => {
                  const key = isoOf(d);
                  const row = byDate[key] ?? EMPTY;
                  const inherited = row.volume.trim() === "" ? week[d.getUTCDay()] : undefined;
                  const set = (r: Row) => setByDate((prev) => ({ ...prev, [key]: r }));
                  const label = d.toLocaleDateString("pt-BR", { weekday: "short", day: "2-digit", month: "2-digit", timeZone: "UTC" });
                  return (
                    <tr key={key}>
                      <td>{label}</td>
                      <td>
                        <input
                          type="number"
                          min={0}
                          className="dpInput"
                          aria-label={`Volume de ${label}`}
                          placeholder={inherited?.volume || ""}
                          value={row.volume}
                          onChange={(e) => set({ ...row, volume: e.target.value })}
                          disabled={busy}
                        />
                      </td>
                      <td>{pctInput(row, set, `% da manhã de ${label}`)}</td>
                      <td>{hint(inherited?.volume ? inherited : row)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {error ? <p className="dpNotice dpNotice--warning">{error}</p> : null}
        </div>
        <footer className="dpModal__footer">
          <button type="button" className="dpBtn dpBtn--secondary" onClick={onClose} disabled={busy}>
            Cancelar
          </button>
          <button type="button" className="dpBtn dpBtn--primary" onClick={save} disabled={busy}>
            Salvar volumes
          </button>
        </footer>
      </div>
    </div>
  );
}
